import { format_datetime_dd_mmm_yy as fmt_datetime } from '../../../utils/format_date_dd_mmm_yy';
import React, { useCallback, useEffect, useState } from 'react';
import { Badge, Button, Card, CardBody, Input } from 'reactstrap';
import cs_analysis_fetch from '../../../utils/cs_analysis_fetch';
import TaskCreateModal from './TaskCreateModal';
import TaskComments from './TaskComments';
import LinkedClientTickets from './LinkedClientTickets';
import TaskLogActivity from './TaskLogActivity';
import { due_color, is_overdue, is_due_soon } from '../../../utils/task_due';
import { TaskBtn, SortToggle, TASK_STATUS_COLOR as STATUS_COLOR } from './task_ui';

// Terminal statuses have no forward action except Reopen.
const TERMINAL = new Set(['DONE', 'DISMISSED']);

// Rep lifecycle: OPEN -> IN_PROGRESS -> DONE (+ DEFERRED = snoozed, DISMISSED =
// dropped). ACCEPTED is retired from the rep UI; it stays in the enum only so
// any legacy ACCEPTED rows remain visible + actionable (treated like OPEN).
const STATUS_FILTERS = ['OPEN', 'IN_PROGRESS', 'DONE', 'DEFERRED', 'DISMISSED'];

// Status dropdown options = the filter set (so filters + row control match
// exactly), plus any legacy status the row is currently in (e.g. ACCEPTED) so
// it still displays + can be changed away from.
const STATUS_OPTIONS_FOR = (current) =>
    STATUS_FILTERS.includes(current) ? STATUS_FILTERS : [current, ...STATUS_FILTERS];

// Active/Archive bucketing (mirrors Findings). Active = the live worklist:
// OPEN / IN_PROGRESS (+ legacy ACCEPTED), plus DEFERRED whose revisit date is
// today-or-past (auto-resurfaced at read time — no cron, status stays
// rep-controlled). Archive = DONE / DISMISSED + DEFERRED still snoozed forward.
const is_active_task = (r) => {
    if (r.status === 'DONE' || r.status === 'DISMISSED') return false;
    if (r.status === 'DEFERRED') return !r.snoozedUntil || new Date(r.snoozedUntil).getTime() <= Date.now();
    return true;
};

// user_details / store_info / set_page_loader come from StoreDashboard and feed
// the task card's "Log call / meeting" (native AddEditActivity); all optional —
// the card fetches what it lacks on demand.
const Actionables = ({ store_id, user_details, store_info, set_page_loader }) => {
    const [rows, set_rows] = useState([]);
    const [bucket, set_bucket] = useState('ACTIVE'); // 'ACTIVE' | 'ARCHIVE'
    const [err, set_err] = useState(null);
    const [busy_id, set_busy_id] = useState(null);
    const [modal_open, set_modal_open] = useState(false);
    const [sort_by, set_sort_by] = useState('due'); // 'due' | 'status' | 'owner'
    const [reps, set_reps] = useState([]);
    const [rep_role, set_rep_role] = useState('rep'); // 'rep' | 'lead' | 'admin'
    const [rep_self, set_rep_self] = useState(null);

    const load = useCallback(async () => {
        set_err(null);
        try {
            const resp = await cs_analysis_fetch({
                path: `/api/v1/clients/${encodeURIComponent(store_id)}/actionables`,
                query: {}, // fetch all; bucket into Active/Archive client-side
            });
            set_rows(Array.isArray(resp) ? resp : []);
        } catch (e) {
            set_err(e.message || String(e));
        }
    }, [store_id]);

    useEffect(() => {
        load();
    }, [load]);

    // CS reps for the owner-assignment dropdown on each card (fetched once).
    useEffect(() => {
        (async () => {
            try {
                const r = await cs_analysis_fetch({ path: '/api/v1/cs/reps' });
                set_reps(r && Array.isArray(r.reps) ? r.reps : []);
                set_rep_role(r && r.role ? r.role : 'rep');
                set_rep_self(r && r.self ? r.self : null);
            } catch (_e) { /* non-fatal — owner select still shows current + unassigned */ }
        })();
    }, []);

    // Surgical row update — keeps inline-editing state intact. Failed PATCH
    // shows the error but doesn't reset the table.
    const patch = async (id, body) => {
        set_busy_id(id);
        try {
            const next = await cs_analysis_fetch({
                path: `/api/v1/clients/${encodeURIComponent(store_id)}/actionables/${id}`,
                method: 'PATCH',
                body,
            });
            set_rows((curr) => curr.map((r) => (r.id === id ? { ...r, ...next } : r)));
        } catch (e) {
            set_err(e.message || String(e));
        } finally {
            set_busy_id(null);
        }
    };

    const active_count = rows.filter(is_active_task).length;
    const visible = [...rows]
        .filter((r) => (bucket === 'ACTIVE' ? is_active_task(r) : !is_active_task(r)))
        .sort((a, b) => {
            if (sort_by === 'owner') return (a.owner || '~~~').localeCompare(b.owner || '~~~');
            if (sort_by === 'status') return STATUS_FILTERS.indexOf(a.status) - STATUS_FILTERS.indexOf(b.status);
            const ta = a.dueDate ? new Date(a.dueDate).getTime() : Infinity;
            const tb = b.dueDate ? new Date(b.dueDate).getTime() : Infinity;
            return ta - tb; // soonest / overdue first; undated last
        });

    return (
        <Card>
            <CardBody>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, gap: 12, flexWrap: 'wrap' }}>
                    <div style={{ display: 'flex', gap: 4 }}>
                        {[{ key: 'ACTIVE', label: 'Active', n: active_count }, { key: 'ARCHIVE', label: 'Archive', n: rows.length - active_count }].map((b) => (
                            <button
                                key={b.key}
                                type='button'
                                onClick={() => set_bucket(b.key)}
                                style={{
                                    fontSize: 12, fontWeight: 600, letterSpacing: 0.3, padding: '5px 14px',
                                    borderRadius: 999, cursor: 'pointer',
                                    border: '1px solid ' + (bucket === b.key ? '#2f6fed' : '#d7dce3'),
                                    background: bucket === b.key ? '#2f6fed' : '#fff',
                                    color: bucket === b.key ? '#fff' : '#57606f',
                                }}
                            >
                                {b.label} {b.n}
                            </button>
                        ))}
                    </div>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                        <SortToggle
                            value={sort_by}
                            onChange={set_sort_by}
                            options={[{ value: 'due', label: 'Due' }, { value: 'status', label: 'Status' }, { value: 'owner', label: 'Owner' }]}
                        />
                        <TaskBtn variant='primary' onClick={() => set_modal_open(true)}>+ New Task</TaskBtn>
                        <TaskBtn variant='link' onClick={load}>↻ Refresh</TaskBtn>
                    </div>
                </div>
                {err ? <div style={{ color: '#c0392b', marginBottom: 8 }}>{err}</div> : null}
                {visible.length === 0 ? (
                    <div style={{ color: '#7f8c8d', textAlign: 'center', padding: 24 }}>No {bucket === 'ACTIVE' ? 'active' : 'archived'} tasks</div>
                ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
                        {visible.map((r) => (
                            <TaskCard
                                key={r.id}
                                row={r}
                                busy={busy_id === r.id}
                                on_patch={patch}
                                store_id={store_id}
                                reps={reps}
                                rep_role={rep_role}
                                self_email={rep_self ? rep_self.email : ''}
                                user_details={user_details}
                                store_info={store_info}
                                set_page_loader={set_page_loader}
                                on_row_change={(next) => set_rows((curr) => curr.map((x) => (next && x.id === next.id ? { ...x, ...next } : x)))}
                            />
                        ))}
                    </div>
                )}
            </CardBody>
            <TaskCreateModal
                open={modal_open}
                store_id={store_id}
                reps={reps}
                rep_role={rep_role}
                self_email={rep_self ? rep_self.email : ''}
                on_close={() => set_modal_open(false)}
                on_created={() => { set_modal_open(false); load(); }}
            />
        </Card>
    );
};

/**
 * One row + an expandable rep-note sub-row. Splitting into a component so each
 * row has its own draft state for the textarea (no cross-row interference).
 */
export const TaskCard = ({ row, busy, on_patch, store_id, reps, rep_role, self_email = '', user_details, store_info, set_page_loader, on_row_change }) => {
    const overdue_flag = !TERMINAL.has(row.status) && row.dueDate && (is_overdue(row.dueDate) || is_due_soon(row.dueDate));
    const owner_known = !row.owner || reps.some((rp) => rp.email === row.owner);
    // Edit rights: only the task owner, a CS lead (over the owner / unassigned in
    // her team), or an admin can mutate the task. Everyone else sees it read-only.
    const can_edit = rep_role === 'admin'
        || (!!self_email && row.owner === self_email)
        || (rep_role === 'lead' && (!row.owner || reps.some((rp) => rp.email === row.owner)));
    const locked = busy || !can_edit;
    return (
        <div style={{ border: '1px solid #e4e8ee', borderLeft: `4px solid ${STATUS_COLOR[row.status] || '#95a5a6'}`, borderRadius: 10, padding: 16, background: '#fff', boxShadow: '0 2px 8px rgba(23,43,77,0.09)' }}>
            {/* Top line — aligned, scannable columns: status · module · title (flex) · owner · due */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                <span style={{ width: 8, height: 8, borderRadius: '50%', background: STATUS_COLOR[row.status] || '#95a5a6', flexShrink: 0 }} />
                <Input
                    type='select'
                    bsSize='sm'
                    value={row.status}
                    disabled={locked}
                    title={can_edit ? 'Change status' : 'Read-only — only the owner, a CS lead, or an admin can edit'}
                    onChange={(e) => on_patch(row.id, { status: e.target.value })}
                    style={{ fontSize: 12, width: 128, fontWeight: 600, color: STATUS_COLOR[row.status] || '#57606f', padding: '2px 6px', flexShrink: 0 }}
                >
                    {STATUS_OPTIONS_FOR(row.status).map((s) => (
                        <option key={s} value={s}>{s.replace('_', ' ')}</option>
                    ))}
                </Input>
                <span style={{ fontSize: 11, color: '#7f8c8d', letterSpacing: 0.3, flexShrink: 0 }}>{row.module}</span>
                {!can_edit ? (
                    <span title='Only the owner, a CS lead, or an admin can edit this task' style={{ fontSize: 10, color: '#8a94a6', background: '#f1f3f6', border: '1px solid #e4e8ee', borderRadius: 4, padding: '1px 6px', flexShrink: 0, textTransform: 'uppercase', letterSpacing: 0.4 }}>view only</span>
                ) : null}
                <div style={{ flex: 1, minWidth: 200 }}>
                    <span style={{ fontWeight: 600, fontSize: 14 }}>{row.title || row.description}</span>
                    {row.repTriagedAt ? (
                        <span title={`Triaged ${fmt_datetime(row.repTriagedAt)} by ${row.repTriagedBy || 'rep'}`} style={{ color: '#27ae60', fontSize: 12, marginLeft: 6 }}>✓</span>
                    ) : null}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>
                    <span style={{ fontSize: 11, color: '#8a94a6' }}>owner</span>
                    <Input
                        type='select'
                        bsSize='sm'
                        value={row.owner || ''}
                        disabled={locked || rep_role === 'rep'}
                        title={rep_role === 'rep' ? 'Only a CS lead or admin can reassign' : 'Assign owner'}
                        onChange={(e) => on_patch(row.id, { owner: e.target.value })}
                        style={{ width: 180, fontSize: 12 }}
                    >
                        <option value=''>unassigned</option>
                        {!owner_known ? <option value={row.owner}>{row.owner}</option> : null}
                        {reps.map((rp) => <option key={rp.userId} value={rp.email}>{rp.name}</option>)}
                    </Input>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>
                    <span style={{ fontSize: 11, color: '#8a94a6' }}>due</span>
                    <Input
                        type='date'
                        bsSize='sm'
                        defaultValue={row.dueDate ? new Date(row.dueDate).toISOString().slice(0, 10) : ''}
                        disabled={locked}
                        onChange={(e) => on_patch(row.id, { dueDate: e.target.value || undefined })}
                        style={{ width: 148, fontSize: 12 }}
                    />
                    {overdue_flag ? (
                        <span style={{ fontSize: 11, fontWeight: 600, color: due_color(row.dueDate) }}>
                            {is_overdue(row.dueDate) ? 'overdue' : 'due soon'}
                        </span>
                    ) : null}
                </div>
            </div>

            {/* Secondary line — description (when a distinct title exists), source, deferred revisit */}
            <div style={{ marginTop: 6, display: 'flex', alignItems: 'baseline', gap: 12, flexWrap: 'wrap' }}>
                {row.title ? <span style={{ fontSize: 13, color: '#3d4852' }}>{row.description}</span> : null}
                {row.finding ? (
                    <span style={{ fontSize: 11, color: '#7f8c8d' }} title={`From finding: ${row.finding.title}`}>
                        🔗 from finding: {row.finding.title.length > 60 ? row.finding.title.slice(0, 60) + '…' : row.finding.title}
                    </span>
                ) : (
                    <span style={{ fontSize: 11, color: '#bdc3c7' }}>manual task</span>
                )}
                {row.status === 'DEFERRED' ? (
                    <span style={{ fontSize: 11 }}>
                        <span style={{ color: '#8e44ad', marginRight: 4 }}>revisit</span>
                        <Input
                            type='date'
                            bsSize='sm'
                            value={row.snoozedUntil ? new Date(row.snoozedUntil).toISOString().slice(0, 10) : ''}
                            disabled={locked}
                            onChange={(e) => on_patch(row.id, { status: 'DEFERRED', snoozedUntil: e.target.value ? new Date(e.target.value).toISOString() : null })}
                            style={{ display: 'inline-block', width: 140, fontSize: 11 }}
                        />
                    </span>
                ) : null}
            </div>

            {/* Linked work + comments side-by-side (uses card width; stacks on narrow) */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 20, marginTop: 8 }}>
                <LinkedWorkPanel
                    actionable={row}
                    store_id={store_id}
                    can_edit={can_edit}
                    user_details={user_details}
                    store_info={store_info}
                    set_page_loader={set_page_loader}
                    on_row_change={on_row_change}
                />
                <TaskComments store_id={store_id} actionable_id={row.id} />
            </div>
        </div>
    );
};

/**
 * Phase C-Lite — "Linked work" panel under an expanded actionable. Shows
 * the live state of the CS Activities and customer_tickets the rep (or
 * the chat LLM) has linked. Each row has an ✕ that DELETEs the link;
 * each subsection has a search-and-discovery box (type to find + pick, or
 * paste a raw id) that POSTs a new link — plus the chat panel's
 * "link activity 1234" intent parsing.
 *
 * Lives inline in Actionables.jsx (rather than a dedicated detail view)
 * because the existing Row already has an expand affordance + per-row
 * state; adding a separate detail route would be more code for the same
 * affordance. If the Linked-work UX grows (e.g. ticket-detail drill-in,
 * status filters), splitting to a sibling file becomes worthwhile.
 */
const LinkedWorkPanel = ({ actionable, store_id, can_edit = true, user_details, store_info, set_page_loader, on_row_change }) => {
    const [data, set_data] = useState(null);
    const [loading, set_loading] = useState(false);
    const [err, set_err] = useState(null);

    const load = useCallback(async () => {
        set_loading(true);
        set_err(null);
        try {
            const resp = await cs_analysis_fetch({
                path: `/api/v1/clients/${encodeURIComponent(store_id)}/actionables/${actionable.id}/linked-items`,
            });
            set_data(resp);
        } catch (e) {
            set_err(e.message || String(e));
        } finally {
            set_loading(false);
        }
    }, [store_id, actionable.id]);

    useEffect(() => {
        load();
    }, [load]);

    // Re-load when the upstream linkedActivityIds / linkedTicketIds arrays change
    // (e.g. after the chat LLM linked something while the row was expanded).
    // Client-ticket links ride on the actionable view itself (linkedClientTickets)
    // — they're in the signature so a link/unlink re-syncs the panel too.
    const link_signature =
        (actionable.linkedActivityIds || []).join(',') + '|' +
        (actionable.linkedTicketIds || []).join(',') + '|' +
        (actionable.linkedClientTickets || []).map((t) => t.id).join(',');
    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [link_signature]);

    // Auto-link items created via the "+ New …" buttons, which open the native
    // Activities/Tickets create in a NEW TAB with ?link_task=<this task>. On save
    // that page broadcasts { taskId, kind, id } on the 'cs_task_link' channel;
    // we link it here + refresh. Same-origin BroadcastChannel — no window.opener
    // needed (so the new tab uses noopener).
    const link_created = useCallback(async (kind, id) => {
        try {
            await cs_analysis_fetch({
                path: `/api/v1/clients/${encodeURIComponent(store_id)}/actionables/${actionable.id}/${kind === 'activity' ? 'link-activity' : 'link-ticket'}`,
                method: 'POST',
                body: kind === 'activity' ? { activityId: String(id) } : { ticketId: String(id) },
            });
            load();
        } catch (_e) { /* non-fatal — rep can still link via search */ }
    }, [store_id, actionable.id, load]);

    useEffect(() => {
        if (typeof BroadcastChannel === 'undefined') return undefined;
        const ch = new BroadcastChannel('cs_task_link');
        ch.onmessage = (e) => {
            const m = e.data || {};
            if (m.taskId === actionable.id && m.id && (m.kind === 'activity' || m.kind === 'ticket')) link_created(m.kind, m.id);
        };
        return () => ch.close();
    }, [actionable.id, link_created]);

    const open_create = (kind) => {
        const path = kind === 'activity' ? '/home/activities' : '/home/customer_tickets';
        const params = new URLSearchParams({
            cs_task_create: '1', kind, org: store_id, link_task: actionable.id,
        });
        // Carry the task's context so the native form opens pre-filled (subject
        // = task title, note = task detail, date = task due date). Guests aren't
        // on the task so they're left for the rep to add.
        if (kind === 'activity') {
            const subject = (actionable.title || actionable.description || '').slice(0, 120);
            if (subject) params.set('subject', subject);
            const note = actionable.notes || actionable.description || '';
            if (note) params.set('note', note);
            if (actionable.dueDate) params.set('due', actionable.dueDate);
        }
        window.open(`${path}?${params.toString()}`, '_blank', 'noopener');
    };

    return (
        <div style={{ marginTop: 14, paddingTop: 10, borderTop: '1px dashed #e1e6ea' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                <div title='Auto-completes this task when all linked items resolve' style={{ fontSize: 11, color: '#7f8c8d', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                    Linked work
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                    {can_edit ? (
                        // Refinement B: the rep logs the call / meeting she did FOR this
                        // task here; the new activity is linked back automatically.
                        <TaskLogActivity
                            store_id={store_id}
                            task={actionable}
                            user_details={user_details}
                            store_info={store_info}
                            set_page_loader={set_page_loader}
                            on_linked={load}
                        />
                    ) : null}
                    <Button size='sm' outline color='link' onClick={load} disabled={loading} style={{ fontSize: 11 }}>
                        {loading ? '…' : 'Refresh'}
                    </Button>
                </div>
            </div>
            {err ? <div style={{ color: '#c0392b', fontSize: 12, marginBottom: 6 }}>{err}</div> : null}
            <LinkedSubsection
                label='Activities'
                kind='activity'
                store_id={store_id}
                actionable_id={actionable.id}
                items={data ? data.activities : []}
                placeholder='Search activities (subject/note) or paste an id…'
                on_changed={load}
                describe_item={describe_activity}
                can_edit={can_edit}
                on_create={() => open_create('activity')}
            />
            <LinkedSubsection
                label='Product Tickets'
                kind='ticket'
                store_id={store_id}
                actionable_id={actionable.id}
                items={data ? data.tickets : []}
                placeholder='Search product tickets (title) or paste an id…'
                on_changed={load}
                describe_item={describe_ticket}
                can_edit={can_edit}
                on_create={() => open_create('ticket')}
            />
            {/* Refinement A: client tickets (cs-analysis ClientTicket) are the third
                linkable kind. Chips come from the actionable view's
                linkedClientTickets; link/unlink return the updated view. */}
            <LinkedClientTickets
                store_id={store_id}
                actionable={actionable}
                can_edit={can_edit}
                on_changed={on_row_change}
            />
        </div>
    );
};

const describe_activity = (a) => {
    const parts = [a.type ? `type=${a.type}` : null, a.dueDate ? `due ${a.dueDate.slice(0, 10)}` : null];
    const meta = parts.filter(Boolean).join(' · ');
    return (a.note ? `${a.note}  ` : '') + (meta ? `(${meta})` : '');
};

const describe_ticket = (t) => {
    const parts = [t.type ? `type=${t.type}` : null];
    const meta = parts.filter(Boolean).join(' · ');
    return (t.title ? `${t.title}  ` : '') + (meta ? `(${meta})` : '');
};

const TERMINAL_BADGE_COLORS = {
    // Activity terminal-ish
    completed: '#27ae60', complete: '#27ae60', done: '#27ae60', closed: '#27ae60',
    cancelled: '#7f8c8d', canceled: '#7f8c8d',
    // Ticket terminal-ish
    resolved: '#27ae60', archived: '#7f8c8d',
    // Soft / in-flight
    open: '#3498db', scheduled: '#3498db', in_progress: '#f39c12',
    missing: '#c0392b', unknown: '#95a5a6',
};

const LinkedSubsection = ({ label, kind, store_id, actionable_id, items, placeholder, on_changed, describe_item, can_edit = true, on_create }) => {
    // Display noun: the linkable 'ticket' kind is the upstream customer_ticket,
    // presented as "product ticket" everywhere (concept rename); prop stays 'ticket'.
    const kind_label = kind === 'ticket' ? 'product ticket' : kind;
    const [draft, set_draft] = useState('');
    const [busy, set_busy] = useState(false);
    const [err, set_err] = useState(null);
    const [results, set_results] = useState([]);
    const [searching, set_searching] = useState(false);
    const [open, set_open] = useState(false);
    // Search box is hidden until the rep clicks "+ Link" — keeps the Linked
    // work area clean when they're just reviewing.
    const [show_search, set_show_search] = useState(false);

    const link_path = kind === 'activity' ? 'link-activity' : 'link-ticket';
    const id_field = kind === 'activity' ? 'activityId' : 'ticketId';
    const search_path = kind === 'activity' ? 'search/activities' : 'search/tickets';

    // Search-and-discovery: type a few chars → matching activities / tickets
    // appear in a dropdown to pick from (no need to know the raw id). Debounced.
    useEffect(() => {
        const q = draft.trim();
        if (q.length < 2) { set_results([]); set_open(false); return undefined; }
        let cancelled = false;
        set_searching(true);
        const t = setTimeout(async () => {
            try {
                const resp = await cs_analysis_fetch({
                    path: `/api/v1/clients/${encodeURIComponent(store_id)}/actionables/${search_path}?q=${encodeURIComponent(q)}`,
                });
                if (cancelled) return;
                set_results(Array.isArray(resp) ? resp : []);
                set_open(true);
            } catch (_e) { if (!cancelled) set_results([]); } finally { if (!cancelled) set_searching(false); }
        }, 300);
        return () => { cancelled = true; clearTimeout(t); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [draft, store_id, search_path]);

    const link_id = async (id) => {
        const v = (id || '').toString().trim();
        if (!v) return;
        set_busy(true);
        set_err(null);
        try {
            await cs_analysis_fetch({
                path: `/api/v1/clients/${encodeURIComponent(store_id)}/actionables/${actionable_id}/${link_path}`,
                method: 'POST',
                body: { [id_field]: v },
            });
            set_draft(''); set_results([]); set_open(false); set_show_search(false);
            on_changed();
        } catch (e) {
            set_err(e.message || String(e));
        } finally {
            set_busy(false);
        }
    };

    const submit = () => link_id(draft);

    const unlink = async (id) => {
        set_busy(true);
        set_err(null);
        try {
            await cs_analysis_fetch({
                path: `/api/v1/clients/${encodeURIComponent(store_id)}/actionables/${actionable_id}/${link_path}/${encodeURIComponent(id)}`,
                method: 'DELETE',
            });
            on_changed();
        } catch (e) {
            set_err(e.message || String(e));
        } finally {
            set_busy(false);
        }
    };

    return (
        <div style={{ marginBottom: 8 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                <span style={{ fontSize: 11, color: '#566573', fontWeight: 600 }}>{label}</span>
                {can_edit ? (
                    <>
                        {on_create ? (
                            <button
                                type='button'
                                title={`Create a new ${kind_label} (opens a new tab; auto-links back on save)`}
                                onClick={on_create}
                                style={{ border: 'none', background: 'transparent', color: '#2f6fed', cursor: 'pointer', fontSize: 11, padding: 0, fontWeight: 600 }}
                            >
                                + new
                            </button>
                        ) : null}
                        <button
                            type='button'
                            title={show_search ? 'Cancel' : `Link an existing ${kind_label}`}
                            onClick={() => { set_show_search((v) => !v); if (show_search) { set_draft(''); set_open(false); } }}
                            style={{ border: 'none', background: 'transparent', color: '#2f6fed', cursor: 'pointer', fontSize: 11, padding: 0, fontWeight: 600 }}
                        >
                            {show_search ? '× cancel' : '+ link'}
                        </button>
                    </>
                ) : null}
            </div>
            {items.length === 0 ? (
                !show_search ? <div style={{ fontSize: 12, color: '#95a5a6', marginBottom: 4 }}>(none linked)</div> : null
            ) : (
                <div style={{ marginBottom: 6 }}>
                    {items.map((it) => {
                        const status = (it.status || 'unknown').toLowerCase();
                        const color = TERMINAL_BADGE_COLORS[status] || '#95a5a6';
                        // Open the actual activity/ticket in a new tab to verify/edit —
                        // the raw internal id is meaningless to the rep. The native page
                        // reads open_activity / open_ticket and pops that item's editor.
                        const href = kind === 'activity'
                            ? `/home/activities?open_activity=${encodeURIComponent(it.id)}`
                            : `/home/customer_tickets?open_ticket=${encodeURIComponent(it.id)}`;
                        const label_text = describe_item(it) || (kind === 'activity' ? (it.subject || 'Open activity') : (it.title || 'Open product ticket'));
                        return (
                            <div key={it.id} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '2px 0', fontSize: 12 }}>
                                <Badge style={{ backgroundColor: color, color: '#fff' }}>{status}</Badge>
                                <a
                                    href={href}
                                    target='_blank'
                                    rel='noreferrer noopener'
                                    title={`Open this ${kind_label} in a new tab to verify / edit`}
                                    style={{ color: '#2f6fed', flex: 1, textDecoration: 'none' }}
                                >
                                    {label_text} <span style={{ fontSize: 10 }}>↗</span>
                                </a>
                                {can_edit ? (
                                    <button
                                        type='button'
                                        title={`Unlink ${kind_label} ${it.id}`}
                                        disabled={busy}
                                        onClick={() => unlink(it.id)}
                                        style={{ border: 'none', background: 'transparent', color: '#c0392b', cursor: 'pointer', fontSize: 13, padding: '0 6px' }}
                                    >
                                        ✕
                                    </button>
                                ) : null}
                            </div>
                        );
                    })}
                </div>
            )}
            {can_edit && show_search ? (
            <div style={{ position: 'relative' }}>
                <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                    <Input
                        bsSize='sm'
                        value={draft}
                        placeholder={placeholder}
                        autoFocus
                        onChange={(e) => set_draft(e.target.value)}
                        onFocus={() => { if (results.length) set_open(true); }}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') { e.preventDefault(); submit(); }
                            if (e.key === 'Escape') set_open(false);
                        }}
                        disabled={busy}
                        style={{ maxWidth: 320, fontSize: 12 }}
                    />
                    <Button size='sm' color='primary' outline disabled={busy || !draft.trim()} onClick={submit}>
                        Link
                    </Button>
                    {searching ? <span style={{ fontSize: 11, color: '#95a5a6' }}>…</span> : null}
                </div>
                {open && results.length > 0 ? (
                    <div style={{ position: 'absolute', zIndex: 20, top: '100%', left: 0, minWidth: 320, maxWidth: 460, maxHeight: 240, overflowY: 'auto', background: '#fff', border: '1px solid #d7dce3', borderRadius: 6, boxShadow: '0 4px 14px rgba(0,0,0,0.1)', marginTop: 2 }}>
                        {results.map((r) => (
                            <button
                                key={r.id}
                                type='button'
                                onClick={() => link_id(r.id)}
                                disabled={busy}
                                style={{ display: 'block', width: '100%', textAlign: 'left', border: 'none', borderBottom: '1px solid #f0f2f5', background: '#fff', padding: '6px 10px', cursor: 'pointer', fontSize: 12 }}
                            >
                                <div style={{ fontWeight: 600, color: '#2c3e50' }}>
                                    {kind === 'activity' ? (r.subject || r.type || r.id) : (r.title || r.id)}
                                </div>
                                <div style={{ color: '#7f8c8d', fontSize: 11 }}>
                                    <code>{r.id}</code>
                                    {r.status ? ` · ${r.status}` : ''}
                                    {kind === 'activity' && r.dueDate ? ` · due ${String(r.dueDate).slice(0, 10)}` : ''}
                                    {kind === 'ticket' && r.priority ? ` · ${r.priority}` : ''}
                                </div>
                            </button>
                        ))}
                    </div>
                ) : null}
            </div>
            ) : null}
            {err ? <div style={{ color: '#c0392b', fontSize: 11, marginTop: 4 }}>{err}</div> : null}
        </div>
    );
};

export default Actionables;
